import { type StateEmailParagraph, StateEmailParagraphState } from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  AgxEmptyState,
  Icon_Arrow,
  Icon_EditOutline,
  AgxSpacer,
  AgxTypography,
  useDialog,
} from '@urbanx/agx.ui-components2';
import SettingsSection from 'components/settings-section/SettingsSection';
import { LexicalDisplay } from 'components/lexical-editor/LexicalEditor';
import 'components/settings-section/SettingsSection.scss';
import EmailParagraphDialog from './EmailParagraphDialog';
import { type EmailType, isBlankParagraph } from './emailTemplateFormDefinition';
import './EmailTemplateSection.scss';

interface Props {
  type: EmailType;
  title: string;
  description: string;
  value: StateEmailParagraph[];
  states: StateEmailParagraphState[];
  disabled: boolean;
  error?: string;
  onChange: (value: StateEmailParagraph[]) => void;
}

export default function EmailTemplateSection({
  type,
  title,
  description,
  value,
  states,
  disabled,
  error,
  onChange,
}: Props) {
  const editor = useDialog(EmailParagraphDialog);

  return (
    <>
      {states.map((state) => {
        const paragraph = value.find((item) => item.state === state)?.paragraph ?? '';
        const hasParagraph = !isBlankParagraph(paragraph);
        const id = `${type}-${state}`;
        const cardTitle = states.length > 1 ? `${title} (${state})` : title;
        const openEditor = () =>
          editor.open({
            title: cardTitle,
            emailType: type,
            state,
            paragraph,
            onApply: (updatedParagraph) => {
              if (isBlankParagraph(updatedParagraph)) {
                onChange(value.filter((item) => item.state !== state));
                return;
              }
              const entry = { state, paragraph: updatedParagraph };
              onChange(
                value.some((item) => item.state === state)
                  ? value.map((item) => (item.state === state ? entry : item))
                  : [...value, entry]
              );
            },
          });

        return (
          <SettingsSection
            key={id}
            id={id}
            title={cardTitle}
            description={description}
            ariaLabel={states.length > 1 ? `${title} ${state}` : title}
          >
            {hasParagraph ? (
              <AgxSpacer gap="spacing-md">
                <AgxTypography type="label" size="medium">
                  Custom Paragraph
                </AgxTypography>
                <AgxSpacer
                  padding={{
                    top: 'spacing-ml',
                    bottom: 'spacing-ml',
                    left: 'spacing-lg',
                    right: 'spacing-lg',
                  }}
                  className="agx-email-template-paragraph-preview"
                >
                  <LexicalDisplay key={paragraph} html={paragraph} />
                </AgxSpacer>
                <AgxButton
                  id={`${id}-edit`}
                  text="Edit"
                  variant="primary"
                  size="small"
                  disabled={disabled}
                  onClick={openEditor}
                  rightIcon={<Icon_EditOutline height={16} width={16} color="currentColor" />}
                />
              </AgxSpacer>
            ) : (
              <AgxSpacer className="agx-settings-empty-content">
                <AgxEmptyState
                  variant="advanced"
                  label=""
                  actionButtons={[
                    {
                      isPrimary: true,
                      label: 'Add custom paragraph',
                      order: 1,
                      disabled,
                      onClick: openEditor,
                      rightIcon: (
                        <Icon_Arrow direction="right" height={22} width={22} color="currentColor" />
                      ),
                    },
                  ]}
                />
              </AgxSpacer>
            )}
            {error && (
              <AgxTypography type="form_error" role="alert">
                {error}
              </AgxTypography>
            )}
          </SettingsSection>
        );
      })}
    </>
  );
}
